import {
    formatDistanceToNow,
    intervalToDuration,
    isDate,
    parseISO,
    ParseISOOptions
} from 'date-fns';
import { STRATEGIES } from './implementations/strategies';
import { DateServiceType, Duration, IDateService } from './interface/date.interface';

export class DateService implements IDateService {
    private _strategy!: IDateService;

    /**
     * Creates an instance of the DateService with the specified date, format, and strategy.
     *
     * @param {Date | string | number} [date=new Date()] - The initial date value, can be a Date object, string, or timestamp.
     * @param {string | Function} [format] - The format or formatting function (optional).
     * @param {DateServiceType} [implementation='date-fns'] - The date handling strategy (default is 'date-fns'). 'moment' is deprecated and will throw an error if used.
     * @throws Will throw an error if the 'moment' implementation is selected or if the specified strategy is not found.
     */
    constructor(
        date: Date | string | number = new Date(),
        format?: string | Function,
        implementation: DateServiceType = 'date-fns'
    ) {
        if (implementation === 'moment')
            throw new Error(`Date service implementation moment is deprecated.`);

        const DATE_SERVICE: new (
            date: Date | string | number,
            format?: string | Function
        ) => IDateService = STRATEGIES[implementation];

        if (!DATE_SERVICE)
            throw new Error(`Date service implementation '${implementation}' not found.`);

        this._strategy = new DATE_SERVICE(date, format);
    }

    /**
     * Adds a specified number of days to the current date object.
     *
     * @param {number} days - The number of days to add.
     * @returns {this} The updated date object.
     */
    public addDays(days: number): this {
        return this._strategy.addDays(days) as this;
    }

    /**
     * Adds a specified number of months to the current date object.
     *
     * @param {number} months - The number of months to add.
     * @returns {this} The updated date object.
     */
    public addMonths(months: number): this {
        return this._strategy.addMonths(months) as this;
    }

    /**
     * Adds a specified number of years to the current date object.
     *
     * @param {number} years - The number of years to add.
     * @returns {this} The updated date object.
     */
    public addYears(years: number): this {
        return this._strategy.addYears(years) as this;
    }

    /**
     * Subtracts a specified number of days from the current date object.
     *
     * @param {number} days - The number of days to subtract.
     * @returns {this} The updated date object.
     */
    public subtractDays(days: number): this {
        return this._strategy.subtractDays(days) as this;
    }

    /**
     * Subtracts a specified number of months from the current date object.
     *
     * @param {number} months - The number of months to subtract.
     * @returns {this} The updated date object.
     */
    public subtractMonths(months: number): this {
        return this._strategy.subtractMonths(months) as this;
    }

    /**
     * Subtracts a specified number of years from the current date object.
     *
     * @param {number} years - The number of years to subtract.
     * @returns {this} The updated date object.
     */
    public subtractYears(years: number): this {
        return this._strategy.subtractYears(years) as this;
    }

    /**
     * Formats the date as a string using the default `toDateString()` method.
     *
     * @returns {string} The formatted date string.
     */
    public format(format?: string): string {
        return this._strategy.format(format);
    }

    /**
     * Converts the current object to a JavaScript Date object.
     *
     * @returns {Date} The date object.
     */
    public toDate(): Date {
        return this._strategy.toDate();
    }

    /**
     * Gets the day of the week, using local time.
     *
     * @returns {number} The day of the month.
     */
    public getDay(): number {
        return this._strategy.getDay();
    }

    /**
     * Gets the month, using local time.
     *
     * @returns {number} The day of the month.
     */
    public getMonth(): number {
        return this._strategy.getMonth();
    }

    /**
     * Returns the year for the current date object.
     *
     * @returns {number} The year.
     */
    public getYear(): number {
        return this._strategy.getYear();
    }

    /**
     * Sets the day of the month for the current date object.
     *
     * @param {number} date - The day of the month to set.
     * @returns {this} The updated date object.
     */
    public setDates(date: number): this {
        return this._strategy.setDates(date) as this;
    }

    /**
     * Sets the month for the current date object.
     *
     * @param {number} month - The month to set (0-11).
     * @returns {this} The updated date object.
     */
    public setMonths(month: number): this {
        return this._strategy.setMonths(month) as this;
    }

    /**
     * Sets the year for the current date object.
     *
     * @param {number} year - The year to set.
     * @returns {this} The updated date object.
     */
    public setYears(year: number): this {
        return this._strategy.setYears(year) as this;
    }

    /**
     * Sets the time to the start of the day (00:00:00.000).
     *
     * @returns {this} The updated date object.
     */
    public startOfDay(): this {
        return this._strategy.startOfDay() as this;
    }

    /**
     * Sets the date to the start of the month and the time to the start of the day.
     *
     * @returns {this} The updated date object.
     */
    public startOfMonth(): this {
        return this._strategy.startOfMonth() as this;
    }

    /**
     * Sets the date to the start of the year and the time to the start of the day.
     *
     * @returns {this} The updated date object.
     */
    public startOfYear(): this {
        return this._strategy.startOfYear() as this;
    }

    /**
     * Determines if the current date is the same day as the provided date.
     *
     * @param {Date} date - The date to compare.
     * @returns {boolean} True if both dates are on the same day, false otherwise.
     */
    public isSameDay(date: this): boolean {
        return this._strategy.isSameDay(date);
    }

    /**
     * Determines if the current date is in the same month as the provided date.
     *
     * @param {Date} date - The date to compare.
     * @returns {boolean} True if both dates are in the same month, false otherwise.
     */
    public isSameMonth(date: this): boolean {
        return this._strategy.isSameMonth(date);
    }

    /**
     * Determines if the current date object is before the provided date.
     *
     * @param {Date} date - The date to compare with the current date object.
     * @returns {boolean} True if the current date is before the provided date, false otherwise.
     */
    public isBefore(date: this): boolean {
        return this._strategy.isBefore(date);
    }

    /**
     * Determines if the current date object is after the provided date.
     *
     * @param {Date} date - The date to compare with the current date object.
     * @returns {boolean} True if the current date is after the provided date, false otherwise.
     */
    public isAfter(date: this): boolean {
        return this._strategy.isAfter(date);
    }

    /**
     * Determines if the current date object is same or before the provided date.
     *
     * @param {Date} date - The date to compare with the current date object.
     * @returns {boolean} True if the current date is same or before the provided date, false otherwise.
     */
    public isSameOrBefore(date: this | Date): boolean {
        return this._strategy.isSameOrBefore(date);
    }

    /**
     * Calculates the difference in days between the current date object and a provided date.
     * The difference is rounded up to the nearest whole day.
     *
     * @param {Date} date - The date to compare with the current date object.
     * @returns {number} The number of days difference as a positive integer.
     */
    public differenceInDays(date: this | Date): number {
        return this._strategy.differenceInDays(date);
    }

    /**
     * Calculates the difference in months between the current date object and a provided date.
     *
     * @param {Date} date - The date to compare with the current date object.
     * @returns {number} The number of months difference as a positive integer.
     */
    public differenceInMonths(date: this | Date): number {
        return this._strategy.differenceInMonths(date);
    }

    /**
     * Returns true if the given value is an instance of Date.
     * @param value The value to check
     * @returns True if the given value is a date
     */
    public static isDate(value: unknown): boolean {
        return isDate(value);
    }

    /**
     * Parse the given string in ISO 8601 format and return an instance of Date.
     * Function accepts complete ISO 8601 formats as well as partial implementations. ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
     * If the argument isn't a string, the function cannot parse the string or the values are invalid, it returns Invalid Date.
     * @param value The value to convert
     * @returns The parsed date in the local time zone
     */
    public static parseISO(
        value: string,
        options?: ParseISOOptions<Date> | undefined
    ): Date {
        return parseISO(value, options);
    }

    /**
     * Convert an interval object to a duration object.
     * @param start The interval start to convert to duration
     * @param end The interval end to convert to duration
     * @returns The duration object
     */
    public static intervalToDuration(start: Date, end: Date): Duration {
        return intervalToDuration({ start, end });
    }

    /**
     * Return the distance between the given date and now in words.
     * @param date The given date
     * @returns The distance in words
     */
    public static fromNow(date: Date): string {
        return formatDistanceToNow(date, { addSuffix: true });
    }

    /////////////////////////////////////////////////////////////////////////////////

    toString(): string {
        return this._strategy.toString();
    }
    toDateString(): string {
        return this._strategy.toDateString();
    }
    toTimeString(): string {
        return this._strategy.toTimeString();
    }
    toLocaleString(): string;
    toLocaleString(
        locales?: string | string[],
        options?: Intl.DateTimeFormatOptions
    ): string;
    toLocaleString(locales?: unknown, options?: unknown): string {
        return this._strategy.toLocaleString(
            locales as string | string[],
            options as any
        );
    }
    toLocaleDateString(): string;
    toLocaleDateString(
        locales?: string | string[],
        options?: Intl.DateTimeFormatOptions
    ): string;
    toLocaleDateString(locales?: unknown, options?: unknown): string {
        return this._strategy.toLocaleDateString(
            locales as string | string[],
            options as any
        );
    }
    toLocaleTimeString(): string;
    toLocaleTimeString(
        locales?: string | string[],
        options?: Intl.DateTimeFormatOptions
    ): string;
    toLocaleTimeString(locales?: unknown, options?: unknown): string {
        return this._strategy.toLocaleTimeString(
            locales as string | string[],
            options as any
        );
    }
    valueOf(): number {
        return this._strategy.valueOf();
    }
    getTime(): number {
        return this._strategy.getTime();
    }
    getFullYear(): number {
        return this._strategy.getFullYear();
    }
    getUTCFullYear(): number {
        return this._strategy.getUTCFullYear();
    }
    getUTCMonth(): number {
        return this._strategy.getUTCMonth();
    }
    getDate(): number {
        return this._strategy.getDate();
    }
    getUTCDate(): number {
        return this._strategy.getUTCDate();
    }
    getUTCDay(): number {
        return this._strategy.getUTCDay();
    }
    getHours(): number {
        return this._strategy.getHours();
    }
    getUTCHours(): number {
        return this._strategy.getUTCHours();
    }
    getMinutes(): number {
        return this._strategy.getMinutes();
    }
    getUTCMinutes(): number {
        return this._strategy.getUTCMinutes();
    }
    getSeconds(): number {
        return this._strategy.getSeconds();
    }
    getUTCSeconds(): number {
        return this._strategy.getUTCSeconds();
    }
    getMilliseconds(): number {
        return this._strategy.getMilliseconds();
    }
    getUTCMilliseconds(): number {
        return this._strategy.getUTCMilliseconds();
    }
    getTimezoneOffset(): number {
        return this._strategy.getTimezoneOffset();
    }
    setTime(time: number): number {
        return this._strategy.setTime(time);
    }
    setMilliseconds(ms: number): number {
        return this._strategy.setMilliseconds(ms);
    }
    setUTCMilliseconds(ms: number): number {
        return this._strategy.setUTCMilliseconds(ms);
    }
    setSeconds(sec: number, ms?: number): number {
        return this._strategy.setSeconds(sec, ms);
    }
    setUTCSeconds(sec: number, ms?: number): number {
        return this._strategy.setUTCSeconds(sec, ms);
    }
    setMinutes(min: number, sec?: number, ms?: number): number {
        return this._strategy.setMinutes(min, sec, ms);
    }
    setUTCMinutes(min: number, sec?: number, ms?: number): number {
        return this._strategy.setUTCMinutes(min, sec, ms);
    }
    setHours(hours: number, min?: number, sec?: number, ms?: number): number {
        return this._strategy.setHours(hours, min, sec, ms);
    }
    setUTCHours(hours: number, min?: number, sec?: number, ms?: number): number {
        return this._strategy.setUTCHours(hours, min, sec, ms);
    }
    setDate(date: number): number {
        return this._strategy.setDate(date);
    }
    setUTCDate(date: number): number {
        return this._strategy.setUTCDate(date);
    }
    setMonth(month: number, date?: number): number {
        return this._strategy.setMonth(month, date);
    }
    setUTCMonth(month: number, date?: number): number {
        return this._strategy.setUTCMonth(month, date);
    }
    setFullYear(year: number, month?: number, date?: number): number {
        return this._strategy.setFullYear(year, month, date);
    }
    setUTCFullYear(year: number, month?: number, date?: number): number {
        return this._strategy.setUTCFullYear(year, month, date);
    }
    toUTCString(): string {
        return this._strategy.toUTCString();
    }
    toISOString(): string {
        return this._strategy.toISOString();
    }
    toJSON(key?: any): string {
        return this._strategy.toJSON(key);
    }
    [Symbol.toPrimitive](hint: 'default'): string;
    [Symbol.toPrimitive](hint: 'string'): string;
    [Symbol.toPrimitive](hint: 'number'): number;
    [Symbol.toPrimitive](hint: string): string | number;
    [Symbol.toPrimitive](hint: unknown): string | number {
        throw new Error('Method not implemented.');
    }
}

export const dateService = (
    date?: Date | string | number,
    format?: string | Function,
    type?: DateServiceType
) => new DateService(date, format, type);
